// Thunk usage with typescript:
// Source: https://redux-toolkit.js.org/usage/usage-with-typescript

import {createAsyncThunk, createSlice, Draft, PayloadAction} from "@reduxjs/toolkit";
import axios, {AxiosError} from 'axios';
import {CartItemType, cartService} from "../../api/cart";
import {
    CommercePlatformCartServiceControllersV1ValidatorsCartStatusCodeResult,
    CommercePlatformCartServiceModelsDTOsPublicCartItemDTO,
    CommercePlatformCartServiceModelsDTOsPublicCartItemMiniDTO,
    CommercePlatformCartServiceModelsDTOsPublicCartItemResourceDTO,
    CommercePlatformCartServiceModelsResponsesCartCartResponse,
    GPAWebDataTypesResourceType
} from "../../api/openApi/cartService";
import {CartServiceResponseType} from "../../definitions/enums";
import {webService} from "../../api/web";
import {clearArtworkFile, clearArtworkNotes} from "./addToCart";
import {Nullable, OffCanvasProductType, PromotionType} from "../../definitions/types";

const DEFAULT_ERROR_TYPE = {
    response: {},
    status: 500,
    errorType: CartServiceResponseType.TransientError
} as CartResponseType

type CreateItemRequest = {
    currencyCode: string,
    catalogId: number,
    item: CartItemType
}

type CreateItemsRequest = {
    currencyCode: string,
    catalogId: number,
    items: CartItemType[]
}

const handleAxiosError = (error: any): CartResponseType => {
    if (!axios.isAxiosError(error)) return DEFAULT_ERROR_TYPE;

    const axiosError = error as AxiosError<CommercePlatformCartServiceModelsResponsesCartCartResponse>;
    if (axiosError.response) {
        return {
            response: axiosError.response.data,
            status: axiosError.response.status,
            errorType: CartServiceResponseType.TransientError,
        };
    }

    return DEFAULT_ERROR_TYPE;
};

const validateApiResponse = (response: any, successStatus: number): CartResponseType => {
    if (response.status !== successStatus) {
        return {
            response: response.data,
            status: response.status,
            errorType: CartServiceResponseType.FailedToCreateCartItem,
        };
    }

    return {
        response: response.data,
        status: response.status,
        errorType: CartServiceResponseType.Success,
    };
};

const processResource = async (item: any) => {
    // Intentionally don't handle errors here.
    // If there is an error, user will have a chance to re-add resources on the off canvas.
    // Later, we may add specific error handling for resources.
    if (item.resources && item.resources.length > 0) {
        const response = await cartService.createItemResources(item);
        const validationResult = validateApiResponse(response, 201);
        if (validationResult.errorType === CartServiceResponseType.Success) {
            return response;
        }
    }
    return null;
};

const processPromotion = async (item: any) => {
    // Intentionally don't handle errors here.
    // Later, we may add specific error handling for promotions.
    if (item.promotionCode) {
        const response = await cartService.applyCoupon(item);
        const validationResult = validateApiResponse(response, 200);
        if (validationResult.errorType === CartServiceResponseType.Success) {
            return response;
        }
    }
    return null;
};


const createItemAsync = createAsyncThunk<CartResponseType, CreateItemRequest, { rejectValue: CartResponseType }>(
    'cart/createItem',
    async ({currencyCode, catalogId, item}, {dispatch, rejectWithValue, fulfillWithValue}) => {
        try {
            let cartResponse = await cartService.createItem(currencyCode, catalogId, item);

            // Validate the create cart item response
            const validationResult = validateApiResponse(cartResponse, 201);
            if (validationResult.errorType !== CartServiceResponseType.Success)
                return rejectWithValue(validationResult);

            // Attempt to create resources for the cart item
            const resourceResponse = await processResource(item);

            // Add error handling here if needed
            if (resourceResponse) cartResponse = resourceResponse;

            // Attempt to create a promotion for the cart item
            const promotionResponse = await processPromotion(item);

            // Add error handling here if needed
            if (promotionResponse) cartResponse = promotionResponse;

            // Refresh the cart partial within the MVC application
            await webService.cart.reload();

            // Clear the artwork after adding to cart
            dispatch(clearArtworkFile());
            dispatch(clearArtworkNotes());

            // Return the response for the last successful request
            return fulfillWithValue({
                errorType: CartServiceResponseType.Success,
                response: cartResponse.data,
                status: cartResponse.status
            });

        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const createItemsAsync = createAsyncThunk<CartResponseType, CreateItemsRequest, { rejectValue: CartResponseType }>(
    'cart/createItems',
    async ({currencyCode, catalogId, items}, {dispatch, rejectWithValue, fulfillWithValue}) => {
        try {
            const cartItemResponse = await cartService.createItems(currencyCode, catalogId, items);

            const validationResult = validateApiResponse(cartItemResponse, 201);

            // Validate the creation of cart items response
            if (validationResult.errorType !== CartServiceResponseType.Success)
                return rejectWithValue(validationResult);

            // Refresh the cart partial within the MVC application
            await webService.cart.reload();

            // Clear the artwork after adding to cart
            dispatch(clearArtworkFile());
            dispatch(clearArtworkNotes());

            return fulfillWithValue({
                errorType: CartServiceResponseType.Success,
                response: cartItemResponse.data,
                status: cartItemResponse.status
            });
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const createItemResources = createAsyncThunk<CartResponseType, CartItemType, { rejectValue: CartResponseType }>(
    'cart/createItemResources',
    async (item, {rejectWithValue, fulfillWithValue}) => {
        try {
            const {data, status} = await cartService.createItemResources(item);
            return fulfillWithValue({
                errorType: CartServiceResponseType.Success,
                response: data,
                status
            });
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const applyCoupon = createAsyncThunk<CartResponseType, CartItemType, { rejectValue: CartResponseType }>(
    'cart/applyCoupon',
    async (item, {rejectWithValue, fulfillWithValue}) => {
        try {
            const {data, status} = await cartService.applyCoupon(item);
            return fulfillWithValue({
                errorType: CartServiceResponseType.Success,
                response: data,
                status
            });
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

export type CartStateType = {
    productId?: number
    cartId?: string
    cartItemId?: number
    cartItemPrice?: number
    cartItemQuantity?: number,
    cartStatusCode?: CommercePlatformCartServiceControllersV1ValidatorsCartStatusCodeResult,
    subtotal?: number
    itemCount?: number
    success?: boolean
    cartActionPending?: boolean
    offCanvasPending?: boolean
    request?: CartStateRequestType
    artworkName?: string | null,
    artworkId?: number | null,
    artworkNotes?: string | null,
    artworkPreview?: string | null,
    width?: number | null,
    height?: number | null,
    errorType?: CartServiceResponseType,
    resources?: CartStateItemResource[],
    promotionEvaluation?: CartPromotionEvaluationType,
    show?: boolean,
    product?: Nullable<OffCanvasProductType>,
    promotion?: Nullable<PromotionType>
}

export type CartStateRequestType = {
    productId: number
    artworkName: string | null,
    artworkNotes: string | null,
    artworkId: number | null,
    width: number | null,
    height: number | null,
    quantity: number | null
}

export type CartStateItemResource = {
    item?: CommercePlatformCartServiceModelsDTOsPublicCartItemMiniDTO;
    id?: number;
    type?: GPAWebDataTypesResourceType;
    cartItemId?: number;
    resourceId?: number;
    price?: number;
}

const PromotionEvaluationStatus = {
    Unknown: 'Unknown',
    Valid: 'Valid',
    Invalid: 'Invalid',
    Expired: 'Expired',
    NotStarted: 'NotStarted',
    DoesntApplyItems: 'DoesntApplyItems',
    UnderMinimumOrderValue: 'UnderMinimumOrderValue',
    NotEligible: 'NotEligible',
    Redeemed: 'Redeemed'
} as const;

export type CartPromotionEvaluationStatusType = typeof PromotionEvaluationStatus[keyof typeof PromotionEvaluationStatus];

export const CartPromotionType = {
    Unknown: 'Unknown',
    SingleProduct: 'SingleProduct',
    ProductGroup: 'ProductGroup',
    SingleUseCode: 'SingleUseCode'
} as const;

export type CartPromotionType = typeof CartPromotionType[keyof typeof CartPromotionType];


export type CartPromotionEvaluationType = {
    status?: CartPromotionEvaluationStatusType;
    number?: string | null;
    type?: CartPromotionType;
    description?: string | null;
    start?: string;
    end?: string;
    minAmount?: number | null;
    minQuantity?: number | null;
}

type CartResponseType = {
    errorType: CartServiceResponseType
    status: number,
    response: CommercePlatformCartServiceModelsResponsesCartCartResponse
};

export const name = "cart";

const initialState: CartStateType = {}

const cart = createSlice({
    name: name,
    initialState: {
        value: initialState
    },
    reducers: {
        initialize: (state) => {
            state.value = initialState;
        },
        setRequest: (state, action: PayloadAction<CartStateRequestType>) => {
            state.value.request = action.payload
        },
        setShow: (state, action: PayloadAction<boolean>) => {
            state.value.show = action.payload
        },
        offCanvasPending: (state, action: PayloadAction<boolean>) => {
            state.value.offCanvasPending = action.payload
        },
        setProduct: (state, action: PayloadAction<undefined | Nullable<OffCanvasProductType>>) => {
            state.value.product = action.payload
        },
        setPromotion: (state, action: PayloadAction<undefined | Nullable<PromotionType>>) => {
            state.value.promotion = action.payload
        }
    },
    extraReducers: (builder) => {
        [
            createItemAsync,
            createItemsAsync,
            createItemResources,
            applyCoupon
        ].forEach(thunk => {
            builder
                .addCase(thunk.pending, (state) => {
                    setLoadingStatus(state);
                })
                .addCase(thunk.fulfilled, (state, action: PayloadAction<CartResponseType>) => {
                    setFulfilledStatus(state, action);
                })
                .addCase(thunk.rejected, (state, action: PayloadAction<any>) => {
                    setRejectedStatus(state, action);
                });
        });

        function findCartItemResources(response: CommercePlatformCartServiceModelsResponsesCartCartResponse, request: Draft<CartStateRequestType> | undefined) {
            return response.cart?.groups?.flatMap(groups => groups.items)
                .flatMap(items => items?.resources)
                .filter((cartItemResource): cartItemResource is CommercePlatformCartServiceModelsDTOsPublicCartItemResourceDTO => cartItemResource !== null)
                .filter(cartItemResource =>
                    cartItemResource.item?.group?.productId == request?.productId &&
                    cartItemResource.item?.artworkId == request?.artworkId &&
                    cartItemResource.item?.width == request?.width &&
                    cartItemResource.item?.height == request?.height);
        }

        const setLoadingStatus = (state: Draft<{ value: CartStateType }>) => {
            state.value = {
                ...state.value,
                cartActionPending: true,
                offCanvasPending: true,
                errorType: CartServiceResponseType.Unknown
            };
        };

        const setFulfilledStatus = (state: Draft<{
            value: CartStateType
        }>, action: PayloadAction<CartResponseType>) => {
            const request = state.value.request;
            const response = action.payload.response;
            const cartItem = findCartItem(response, request);
            const cartStatusCode = mapCartStatusCode(response, request);
            const cartItemResources = findCartItemResources(response, request)

            /**
             * Sum the quantities for all cart items
             * @returns {number }
             */
            function getItemCount(): number {
                return (response.cart.groups?.flatMap(items => items.items)
                    .map(x => x?.quantity || 0)
                    .reduce((acc, curr) => acc + curr, 0)) || 0;
            }

            state.value = {
                ...state.value,
                productId: cartItem?.group?.productId,
                cartId: response.cart.id,
                cartItemId: cartItem?.id,
                cartItemPrice: cartItem?.unitPrice,
                cartItemQuantity: cartItem?.quantity,
                cartStatusCode: cartStatusCode,
                itemCount: getItemCount(),
                subtotal: response.cart.orderTotal,
                artworkId: cartItem?.artworkId,
                artworkName: request?.artworkName,
                artworkNotes: request?.artworkNotes,
                width: request?.width,
                height: request?.height,
                success: true,
                cartActionPending: false,
                errorType: action.payload.errorType,
                resources: cartItemResources,
                promotionEvaluation: response.promotionEvaluation
            };
        };

        /**
         * Set the rejected status for the cart slice. Using type any here because the rejected payload could be anything.
         * Errors that return a cart response should type check the response before attempting operations on it.
         * @param state
         * @param action
         */
        const setRejectedStatus = (state: Draft<{ value: CartStateType }>, action: PayloadAction<any>) => {
            const request = state.value.request;
            const cartStatusCode = mapCartStatusCode(action.payload.response, request);
            state.value = {
                ...state.value,
                success: false,
                cartActionPending: false,
                cartStatusCode: cartStatusCode,
                errorType: action?.payload?.errorType ?? CartServiceResponseType.TransientError
            };
        };

        function mapCartStatusCode(response: any, request: Draft<CartStateRequestType> | undefined) {
            if (isCartResponse(response)) {
                return response.cartStatusCodes.find((x: CommercePlatformCartServiceControllersV1ValidatorsCartStatusCodeResult) =>
                    x.productId == request?.productId &&
                    x.artworkId == request?.artworkId &&
                    x.width == request?.width &&
                    x.height == request?.height
                );
            }

            return undefined;
        }

        /**
         * Type guard for CommercePlatformCartServiceModelsResponsesCartCartResponse
         * @param obj
         */
        function isCartResponse(obj: any): obj is CommercePlatformCartServiceModelsResponsesCartCartResponse {
            return (obj as CommercePlatformCartServiceModelsResponsesCartCartResponse).cart !== undefined;
        }
    }
});

export const findCartItem = (
    response: CommercePlatformCartServiceModelsResponsesCartCartResponse | undefined,
    request: Draft<CartStateRequestType> | undefined
) => {
    return response?.cart.groups?.flatMap(x => x.items)
        .filter((cartItem): cartItem is CommercePlatformCartServiceModelsDTOsPublicCartItemDTO => cartItem !== null)
        .find(x => x.group?.productId == request?.productId && x.artworkId == request?.artworkId && x.width == request?.width && x.height == request?.height);
};


export const cartActions = {
    initialize: cart.actions.initialize,
    setRequest: cart.actions.setRequest,
    createItemAsync,
    createItemsAsync,
    createItemResources
};

export const addToCartOffCanvasActions = {
    setShow: cart.actions.setShow,
    setOffCanvasPending: cart.actions.offCanvasPending,
    setProduct: cart.actions.setProduct,
    setPromotion: cart.actions.setPromotion
};

export default cart.reducer;